import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { businessIntelligenceAPI, exchangeAPI } from '../lib/api';
import EmailDetailModal from '../components/EmailDetailModal';
import {
  FiArrowLeft, FiArrowRight, FiArchive, FiLoader, FiAlertCircle, FiRefreshCw,
  FiCheckCircle, FiXCircle, FiClock, FiFileText, FiPlay, FiCloud,
  FiMail, FiDatabase, FiTag, FiX, FiCheck, FiAlertTriangle,
  FiCalendar, FiDownload, FiEye, FiExternalLink, FiFilter, FiSearch,
  FiBarChart2, FiPaperclip, FiUser, FiDollarSign, FiLink, FiFolder,
} from 'react-icons/fi';
import { AiOutlineFilePdf, AiOutlineFileWord, AiOutlineFileExcel, AiOutlineFileZip } from 'react-icons/ai';
import { BsFiletypeCsv, BsRobot } from 'react-icons/bs';
import ReactMarkdown from 'react-markdown';

// ─── Status configs ───────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  pending:    { icon: FiClock,       color: 'text-gray-500',   bg: 'bg-gray-100',   label: 'Pending' },
  syncing:    { icon: FiRefreshCw,   color: 'text-blue-600',   bg: 'bg-blue-100',   label: 'Syncing',       spin: true },
  processing: { icon: FiLoader,      color: 'text-purple-600', bg: 'bg-purple-100', label: 'Processing AI', spin: true },
  complete:   { icon: FiCheckCircle, color: 'text-green-600',  bg: 'bg-green-100',  label: 'Complete' },
  archived:   { icon: FiArchive,     color: 'text-gray-600',   bg: 'bg-gray-100',   label: 'Archived' },
  error:      { icon: FiXCircle,     color: 'text-red-600',    bg: 'bg-red-100',    label: 'Error' },
};

const AI_STATUS_CONFIG = {
  pending:    { color: 'text-gray-400',   label: 'Pending' },
  processing: { color: 'text-purple-600', label: 'Processing' },
  complete:   { color: 'text-green-600',  label: 'Done' },
  error:      { color: 'text-red-600',    label: 'Error' },
};

const ARCHIVE_DOC_TYPE_COLORS = {
  mails:              'bg-blue-100 text-blue-700',
  attachments:        'bg-indigo-100 text-indigo-700',
  static_documents:   'bg-gray-100 text-gray-700',
  editable_documents: 'bg-amber-100 text-amber-700',
  photos:             'bg-pink-100 text-pink-700',
  images:             'bg-purple-100 text-purple-700',
};

const ARCHIVE_DOC_TYPE_LABELS = {
  mails:              'Mail',
  attachments:        'Attachment',
  static_documents:   'Static Doc',
  editable_documents: 'Editable Doc',
  photos:             'Photo',
  images:             'Image',
};

const URGENCY_COLORS = {
  low:    'bg-gray-100 text-gray-600',
  normal: 'bg-blue-100 text-blue-700',
  high:   'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
};

const ENTITY_ICONS = {
  person:    FiUser,
  company:   FiLink,
  amount:    FiDollarSign,
  date:      FiCalendar,
  reference: FiTag,
};

const SOURCE_ICONS = {
  sharepoint: FiCloud,
  exchange:   FiMail,
  doxolve:    FiDatabase,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getFileIcon(filename, mimeType) {
  const ext = (filename || '').split('.').pop().toLowerCase();
  if (mimeType?.includes('pdf') || ext === 'pdf') return <AiOutlineFilePdf className="w-6 h-6 text-red-500" />;
  if (mimeType?.includes('word') || ['doc', 'docx'].includes(ext)) return <AiOutlineFileWord className="w-6 h-6 text-blue-500" />;
  if (mimeType?.includes('sheet') || mimeType?.includes('excel') || ['xls', 'xlsx'].includes(ext)) return <AiOutlineFileExcel className="w-6 h-6 text-green-600" />;
  if (ext === 'csv') return <BsFiletypeCsv className="w-6 h-6 text-green-600" />;
  if (mimeType?.includes('image') || ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) return <FiFileText className="w-6 h-6 text-purple-500" />;
  if (['zip', 'rar', '7z'].includes(ext)) return <AiOutlineFileZip className="w-6 h-6 text-yellow-600" />;
  return <FiFileText className="w-6 h-6 text-gray-400" />;
}

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${cfg.bg} ${cfg.color}`}>
      <Icon className={`w-4 h-4 ${cfg.spin ? 'animate-spin' : ''}`} />
      {cfg.label}
    </span>
  );
}

function DocTypeBadge({ type, customDocTypes = [] }) {
  const color = ARCHIVE_DOC_TYPE_COLORS[type] || 'bg-gray-100 text-gray-600';
  const label = ARCHIVE_DOC_TYPE_LABELS[type] || customDocTypes.find(t => t.id === type)?.name || type?.replace(/_/g, ' ') || 'Unknown';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>{label}</span>;
}

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(str) {
  if (!str) return '—';
  const d = new Date(str);
  return d.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })
    + ' ' + d.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' });
}

function formatPeriod(start, end) {
  if (!start || !end) return null;
  const s = new Date(start + 'T12:00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' });
  const e = new Date(end + 'T12:00:00').toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
  return `${s} – ${e}`;
}

// ─── Document action buttons (mirrors Search.jsx exactly) ─────────────────────

function DocActions({ doc, onViewEmail }) {
  const [downloading, setDownloading] = useState(false);

  const handleDownloadEml = async () => {
    setDownloading(true);
    try {
      const mailboxId = doc.ai_raw?.mailbox_id;
      if (!mailboxId) throw new Error('No mailbox ID');
      const subject = doc.ai_raw?.title || doc.file_name;
      const response = await exchangeAPI.downloadMessage(mailboxId, doc.external_id, subject);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (subject || 'email').replace(/[<>:"/\\|?*]/g, '_').substring(0, 100) + '.eml';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { a.remove(); window.URL.revokeObjectURL(url); }, 100);
    } catch (err) {
      console.error('Download .eml error:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadFile = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('access_token');
      const url = businessIntelligenceAPI.getDocumentDownloadUrl(doc.id, false);
      // Some documents can't be streamed through the backend (e.g. certain SharePoint
      // items with no direct Graph download URL) — the backend responds with a redirect
      // straight to the file's source instead. `fetch` follows that redirect internally,
      // but the final SharePoint response has no CORS header allowing us to read the
      // body, so `redirect: 'manual'` lets us detect that case and open the document's
      // own source link directly instead (a plain navigation isn't subject to CORS,
      // but it also can't carry our Bearer token, so we can't just re-hit our backend URL).
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, redirect: 'manual' });
      if (res.type === 'opaqueredirect' || res.status === 0) {
        if (doc.file_url) window.open(doc.file_url, '_blank', 'noopener,noreferrer');
        return;
      }
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = doc.file_name || 'document';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      console.error('Download error:', err);
      if (doc.file_url) window.open(doc.file_url, '_blank', 'noopener,noreferrer');
    } finally {
      setDownloading(false);
    }
  };

  if (doc.source === 'exchange') {
    return (
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={() => onViewEmail(doc)}
          disabled={!doc.ai_raw?.mailbox_id}
          className="p-1.5 text-purple-600 hover:bg-purple-50 rounded transition-colors disabled:opacity-30"
          title={doc.ai_raw?.mailbox_id ? 'View email details' : 'Reprocess to restore mailbox link'}
        >
          <FiEye className="w-4 h-4" />
        </button>
        <button
          onClick={handleDownloadEml}
          disabled={downloading || !doc.ai_raw?.mailbox_id}
          className="p-1.5 text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-30"
          title="Download .eml"
        >
          {downloading ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiDownload className="w-4 h-4" />}
        </button>
        {doc.file_url && (
          <button
            onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
            title="Open in Outlook"
          >
            <FiExternalLink className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 flex-shrink-0">
      {doc.file_url && (
        <button
          onClick={() => window.open(doc.file_url, '_blank', 'noopener,noreferrer')}
          className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
          title="Open in source"
        >
          <FiEye className="w-4 h-4" />
        </button>
      )}
      <button
        onClick={handleDownloadFile}
        disabled={downloading}
        className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-40"
        title="Download"
      >
        {downloading ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiDownload className="w-4 h-4" />}
      </button>
    </div>
  );
}

// ─── Documents tab — Search-style card layout ─────────────────────────────────

function DocumentsTab({ sessionId, sessionStatus, customDocTypes = [], excludedDocTypes = [] }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renderLimit, setRenderLimit] = useState(50);
  const [filterType, setFilterType] = useState('all');
  const [search, setSearch] = useState('');
  const [emailDetailDoc, setEmailDetailDoc] = useState(null);
  const RENDER_PAGE = 50;

  const loadDocs = useCallback(async () => {
    try {
      const res = await businessIntelligenceAPI.getAllSessionDocuments(sessionId);
      setDocuments(res.data.documents || []);
      setRenderLimit(RENDER_PAGE);
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  }, [sessionId]);

  useEffect(() => { loadDocs(); }, [loadDocs]);
  useEffect(() => { if (sessionStatus === 'complete') loadDocs(); }, [sessionStatus, loadDocs]);

  // document_type is only a static per-mapping default applied to every document synced from
  // a given mailbox/folder (see bi-sync.service.js) — not a real per-document classification.
  // ai_raw.doc_type is the AI extraction step's actual content-based classification, and (since
  // resolveDocType was fixed to return null instead of an unmapped raw string like "mails")
  // it's now always either a genuinely resolved type or null — never garbage — so it's safe
  // to prefer once AI processing has run. Fall back to document_type only when AI hasn't
  // classified the document yet.
  const getEffectiveType = (d) => d.ai_raw?.doc_type || d.document_type;
  const docTypes = [...Object.keys(ARCHIVE_DOC_TYPE_LABELS), ...customDocTypes.map(t => t.id)]
    .filter(t => !excludedDocTypes.includes(t));
  // A document whose effective type doesn't match any configured predefined/custom type used
  // to be silently dropped from every filter and count here — invisible, not just
  // uncategorized (e.g. a session showing "23 Documents" but only 4 summing across the type
  // chips). Route those into a synthetic 'other' bucket instead so nothing goes missing.
  const isOtherType = (d) => !docTypes.includes(getEffectiveType(d));
  const visibleDocuments = documents;
  const q = search.trim().toLowerCase();
  const typeFiltered = filterType === 'all' ? visibleDocuments
    : filterType === 'other' ? visibleDocuments.filter(isOtherType)
    : visibleDocuments.filter(d => getEffectiveType(d) === filterType);
  const displayed = q
    ? typeFiltered.filter(d => {
        const r = d.ai_raw || {};
        const entityValues = (r.entities || []).map(e => (e.value || '').toLowerCase()).join(' ');
        const tagValues = (r.tags || []).map(t => (t || '').toLowerCase()).join(' ');
        return (d.file_name || '').toLowerCase().includes(q)
          || (r.title || '').toLowerCase().includes(q)
          || (r.summary || '').toLowerCase().includes(q)
          || (r.from_name || '').toLowerCase().includes(q)
          || (r.from_email || '').toLowerCase().includes(q)
          || entityValues.includes(q)
          || tagValues.includes(q);
      })
    : typeFiltered;
  const rendered = displayed.slice(0, renderLimit);

  if (loading) return (
    <div className="flex justify-center py-12">
      <FiLoader className="w-6 h-6 animate-spin text-green-600" />
    </div>
  );

  if (documents.length === 0) return (
    <div className="text-center py-12 text-gray-500">
      <FiFileText className="w-10 h-10 mx-auto mb-3 text-gray-300" />
      <p className="text-sm">No documents synced yet.</p>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Search + type filter bar */}
      <div className="flex flex-col gap-2">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search documents…"
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
        {docTypes.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <FiFilter className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            {['all', ...docTypes, 'other'].map(type => {
              const count = type === 'all' ? visibleDocuments.length
                : type === 'other' ? visibleDocuments.filter(isOtherType).length
                : visibleDocuments.filter(d => getEffectiveType(d) === type).length;
              if (type === 'other' && count === 0) return null;
              const label = type === 'all' ? 'All'
                : type === 'other' ? 'Other / unmatched'
                : ARCHIVE_DOC_TYPE_LABELS[type] || customDocTypes.find(t => t.id === type)?.name || type;
              return (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    filterType === type ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label} ({count})
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Cards — same style as Search results */}
      {rendered.map(doc => {
        const SourceIcon = SOURCE_ICONS[doc.source] || FiFileText;
        return (
          <div key={doc.id} className="p-4 border border-gray-200 rounded-lg hover:border-green-300 transition-colors">
            <div className="flex items-start gap-4">
              <div className="mt-1 flex-shrink-0">
                {doc.source === 'exchange'
                  ? <FiMail className="w-6 h-6 text-purple-500" />
                  : getFileIcon(doc.file_name, doc.mime_type)
                }
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-medium text-gray-900 text-sm">{doc.file_name}</h3>
                  <DocTypeBadge type={getEffectiveType(doc)} customDocTypes={customDocTypes} />
                  {doc.source === 'exchange' && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700">
                      <FiMail className="w-3 h-3" /> Exchange
                    </span>
                  )}
                  {doc.source === 'sharepoint' && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-700">
                      <FiCloud className="w-3 h-3" /> SharePoint
                    </span>
                  )}
                  {doc.source === 'doxolve' && (
                    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                      <FiDatabase className="w-3 h-3" /> Doxolve
                    </span>
                  )}
                </div>

                {/* Exchange metadata — same layout as Search results */}
                {doc.source === 'exchange' && doc.ai_raw && (
                  <div className="mt-1 space-y-0.5">
                    {(doc.ai_raw.from_name || doc.ai_raw.from_email) && (
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">From:</span>{' '}
                        {doc.ai_raw.from_name}
                        {doc.ai_raw.from_email ? ` <${doc.ai_raw.from_email}>` : ''}
                      </p>
                    )}
                    {Array.isArray(doc.ai_raw.to_recipients) && doc.ai_raw.to_recipients.length > 0 && (
                      <p className="text-sm text-gray-500 truncate">
                        <span className="font-medium">To:</span>{' '}
                        {doc.ai_raw.to_recipients.join(', ')}
                      </p>
                    )}
                    {doc.ai_raw.body_preview && (
                      <p className="text-sm text-gray-500 line-clamp-2 mt-1">{doc.ai_raw.body_preview}</p>
                    )}
                  </div>
                )}

                {/* SharePoint / Doxolve — show AI-extracted summary if available */}
                {doc.source !== 'exchange' && doc.ai_raw?.summary && (
                  <p className="text-sm text-gray-500 line-clamp-2 mt-1">{doc.ai_raw.summary}</p>
                )}

                <div className="flex items-center flex-wrap gap-2 mt-2">
                  <span className="text-xs text-gray-500">
                    {formatDate(doc.ai_raw?.received_at || doc.created_at)}
                  </span>
                  {doc.source === 'exchange' && doc.ai_raw?.from_email && (
                    <span className="text-xs px-2 py-0.5 bg-purple-50 text-purple-700 rounded flex items-center gap-1">
                      <FiMail className="w-3 h-3" />{doc.ai_raw.from_email}
                    </span>
                  )}
                  {doc.ai_raw?.has_attachments && (
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded flex items-center gap-1">
                      <FiPaperclip className="w-3 h-3" /> Attachments
                    </span>
                  )}
                  {doc.review_status === 'accepted' && (
                    <span className="inline-flex items-center gap-1 text-xs text-green-600">
                      <FiCheck className="w-3 h-3" /> Accepted
                    </span>
                  )}
                  {doc.ai_status === 'complete' && (
                    <span className="inline-flex items-center gap-1 text-xs text-purple-600">
                      <BsRobot className="w-3 h-3" /> AI Processed
                    </span>
                  )}
                  {doc.ai_status === 'error' && (
                    <span className="text-xs text-red-500">AI Error</span>
                  )}
                </div>
              </div>
              <DocActions doc={doc} onViewEmail={setEmailDetailDoc} />
            </div>
          </div>
        );
      })}

      {rendered.length < displayed.length && (
        <div className="flex justify-center pt-2">
          <button
            onClick={() => setRenderLimit(prev => prev + RENDER_PAGE)}
            className="px-5 py-2 text-sm font-medium text-green-700 border border-green-300 rounded-lg hover:bg-green-50 transition-colors"
          >
            Load more ({displayed.length - rendered.length} remaining)
          </button>
        </div>
      )}

      {emailDetailDoc && (
        <EmailDetailModal
          mailboxId={emailDetailDoc.ai_raw?.mailbox_id}
          messageId={emailDetailDoc.external_id}
          subject={emailDetailDoc.file_name}
          onClose={() => setEmailDetailDoc(null)}
        />
      )}
    </div>
  );
}

// ─── AI Processed tab — rich per-doc analysis ────────────────────────────────

function AiProcessedTab({ sessionId, sessionStatus, customDocTypes = [], excludedDocTypes = [] }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renderLimit, setRenderLimit] = useState(50);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [attachmentsCache, setAttachmentsCache] = useState({});
  const [loadingAttachments, setLoadingAttachments] = useState(null);
  const [downloadingAttachment, setDownloadingAttachment] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const [filterGroup, setFilterGroup] = useState('all');
  const [search, setSearch] = useState('');
  const [emailDetailDoc, setEmailDetailDoc] = useState(null);
  const RENDER_PAGE = 50;

  const loadDocs = useCallback(async () => {
    try {
      const res = await businessIntelligenceAPI.getAllSessionDocuments(sessionId);
      const incoming = (res.data.documents || []).filter(d => d.ai_status === 'complete');
      setDocuments(incoming);
      setRenderLimit(RENDER_PAGE);
    } catch { /* non-fatal */ }
    finally { setLoading(false); }
  }, [sessionId]);

  useEffect(() => { loadDocs(); }, [loadDocs]);
  useEffect(() => { if (sessionStatus === 'complete') loadDocs(); }, [sessionStatus, loadDocs]);

  const handleToggleExpand = async (doc) => {
    const docId = doc.id;
    const isExpanding = expandedDoc !== docId;
    setExpandedDoc(isExpanding ? docId : null);
    if (isExpanding && doc.source === 'exchange' && doc.ai_raw?.has_attachments && !attachmentsCache[docId]) {
      setLoadingAttachments(docId);
      try {
        const res = await exchangeAPI.getAttachments(doc.ai_raw.mailbox_id, doc.external_id);
        const atts = (res.data.attachments || res.data.value || []).filter(a => !a.isInline);
        setAttachmentsCache(prev => ({ ...prev, [docId]: atts }));
      } catch { setAttachmentsCache(prev => ({ ...prev, [docId]: [] })); }
      finally { setLoadingAttachments(null); }
    }
  };

  const handleDownloadAttachment = async (doc, att) => {
    setDownloadingAttachment(att.id);
    try {
      const res = await exchangeAPI.downloadAttachment(doc.ai_raw.mailbox_id, doc.external_id, att.id);
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = att.name;
      document.body.appendChild(a); a.click();
      setTimeout(() => { a.remove(); window.URL.revokeObjectURL(url); }, 100);
    } catch { /* non-fatal */ }
    finally { setDownloadingAttachment(null); }
  };

  // document_type is only a static per-mapping default applied to every document synced from
  // a given mailbox/folder (see bi-sync.service.js) — not a real per-document classification.
  // ai_raw.doc_type is the AI extraction step's actual content-based classification, and (since
  // resolveDocType was fixed to return null instead of an unmapped raw string like "mails")
  // it's now always either a genuinely resolved type or null — never garbage — so it's safe
  // to prefer once AI processing has run. Fall back to document_type only when AI hasn't
  // classified the document yet.
  const getEffectiveType = (d) => d.ai_raw?.doc_type || d.document_type;
  const docTypes = [...Object.keys(ARCHIVE_DOC_TYPE_LABELS), ...customDocTypes.map(t => t.id)]
    .filter(t => !excludedDocTypes.includes(t));
  // A document whose effective type doesn't match any configured predefined/custom type used
  // to be silently dropped from every filter/group/count here — invisible, not just
  // uncategorized. Route those into a synthetic 'other' bucket instead so nothing goes missing.
  const isOtherType = (d) => !docTypes.includes(getEffectiveType(d));
  const visibleDocuments = documents;
  const groupNameForType = (type, d) => customDocTypes.find(t => t.id === type)?.group_name || (d && isOtherType(d) ? 'Other / unmatched' : null);
  const docGroups = [...new Set(customDocTypes.filter(t => !excludedDocTypes.includes(t.id) && t.group_name).map(t => t.group_name))];
  const q = search.trim().toLowerCase();
  const typeFiltered = filterType === 'all' ? visibleDocuments
    : filterType === 'other' ? visibleDocuments.filter(isOtherType)
    : visibleDocuments.filter(d => getEffectiveType(d) === filterType);
  const groupFiltered = filterGroup === 'all' ? typeFiltered
    : filterGroup === 'Other / unmatched' ? typeFiltered.filter(isOtherType)
    : typeFiltered.filter(d => groupNameForType(getEffectiveType(d), d) === filterGroup);
  const displayed = q
    ? groupFiltered.filter(d => {
        const r = d.ai_raw || {};
        const entityValues = (r.entities || []).map(e => (e.value || '').toLowerCase()).join(' ');
        const tagValues = (r.tags || []).map(t => (t || '').toLowerCase()).join(' ');
        return (d.file_name || '').toLowerCase().includes(q)
          || (r.title || '').toLowerCase().includes(q)
          || (r.summary || '').toLowerCase().includes(q)
          || (r.from_name || '').toLowerCase().includes(q)
          || (r.from_email || '').toLowerCase().includes(q)
          || (r.classification?.topic || '').toLowerCase().includes(q)
          || entityValues.includes(q)
          || tagValues.includes(q);
      })
    : groupFiltered;
  const rendered = displayed.slice(0, renderLimit);

  if (loading) return (
    <div className="flex justify-center py-12">
      <FiLoader className="w-6 h-6 animate-spin text-green-600" />
    </div>
  );

  if (documents.length === 0) return (
    <div className="text-center py-12 text-gray-500">
      <BsRobot className="w-10 h-10 mx-auto mb-3 text-gray-300" />
      <p className="text-sm">No AI-processed documents yet. Start the sync and processing pipeline.</p>
    </div>
  );

  return (
    <div className="space-y-3">
      {/* Search + type filter bar */}
      <div className="flex flex-col gap-2">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search AI results…"
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
        {docGroups.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <FiFolder className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            {['all', ...docGroups, 'Other / unmatched'].map(g => {
              const count = g === 'all' ? visibleDocuments.length
                : g === 'Other / unmatched' ? visibleDocuments.filter(isOtherType).length
                : visibleDocuments.filter(d => groupNameForType(getEffectiveType(d), d) === g).length;
              if (g === 'Other / unmatched' && count === 0) return null;
              return (
                <button
                  key={g}
                  onClick={() => setFilterGroup(g)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    filterGroup === g ? 'bg-slate-700 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {g === 'all' ? `All Groups (${count})` : `${g} (${count})`}
                </button>
              );
            })}
          </div>
        )}
        {docTypes.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <FiFilter className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            {['all', ...docTypes, 'other'].map(type => {
              const count = type === 'all' ? visibleDocuments.length
                : type === 'other' ? visibleDocuments.filter(isOtherType).length
                : visibleDocuments.filter(d => getEffectiveType(d) === type).length;
              if (type === 'other' && count === 0) return null;
              const label = type === 'all' ? 'All'
                : type === 'other' ? 'Other / unmatched'
                : ARCHIVE_DOC_TYPE_LABELS[type] || customDocTypes.find(t => t.id === type)?.name || type;
              return (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    filterType === type ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {label} ({count})
                </button>
              );
            })}
          </div>
        )}
      </div>
      <p className="text-xs text-gray-500">{displayed.length}{q || filterType !== 'all' || filterGroup !== 'all' ? ` of ${visibleDocuments.length}` : ''} documents with AI analysis</p>

      {rendered.map(doc => {
        const r = doc.ai_raw || {};
        const isExpanded = expandedDoc === doc.id;
        const entities = Array.isArray(r.entities) ? r.entities : [];
        const actionItems = Array.isArray(r.action_items) ? r.action_items : [];

        return (
          <div key={doc.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            {/* Header row */}
            <button
              onClick={() => handleToggleExpand(doc)}
              className="w-full p-4 flex items-start gap-3 text-left hover:bg-gray-50 transition-colors"
            >
              <BsRobot className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium text-gray-900">{r.title || doc.file_name}</span>
                  <DocTypeBadge type={getEffectiveType(doc)} customDocTypes={customDocTypes} />
                  {r.classification?.urgency && r.classification.urgency !== 'normal' && (
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${URGENCY_COLORS[r.classification.urgency] || ''}`}>
                      {r.classification.urgency}
                    </span>
                  )}
                  {r.classification?.topic && (
                    <span className="text-xs px-2 py-0.5 rounded bg-indigo-50 text-indigo-700">{r.classification.topic}</span>
                  )}
                </div>
                {r.date && <p className="text-xs text-gray-400 mt-0.5">{formatDate(r.date)}</p>}
                {r.summary && (
                  <p className="text-xs text-gray-600 mt-1 line-clamp-2">{r.summary}</p>
                )}
              </div>
              <DocActions doc={doc} onViewEmail={setEmailDetailDoc} />
            </button>

            {/* Expanded detail */}
            {isExpanded && (
              <div className="border-t border-gray-100 px-4 pb-4 pt-3 space-y-4 bg-gray-50">
                {r.summary && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Summary</p>
                    <p className="text-sm text-gray-700">{r.summary}</p>
                  </div>
                )}

                {entities.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Key Entities</p>
                    <div className="flex flex-wrap gap-2">
                      {entities.map((e, i) => {
                        const Icon = ENTITY_ICONS[e.type] || FiTag;
                        return (
                          <span key={i} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-white border border-gray-200 text-gray-700">
                            <Icon className="w-3 h-3 text-gray-400" />
                            <span className="text-gray-400 capitalize">{e.type}:</span>
                            <span className="font-medium">{e.value}</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}

                {actionItems.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Action Items</p>
                    <ul className="space-y-1">
                      {actionItems.map((item, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                          <FiAlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {Array.isArray(r.tags) && r.tags.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Tags</p>
                    <div className="flex flex-wrap gap-1">
                      {r.tags.map((tag, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">{tag}</span>
                      ))}
                    </div>
                  </div>
                )}

                {doc.source === 'exchange' && doc.ai_raw?.has_attachments && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Attachments</p>
                    {loadingAttachments === doc.id ? (
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <FiLoader className="w-3.5 h-3.5 animate-spin" /> Loading attachments…
                      </div>
                    ) : (attachmentsCache[doc.id] || []).length === 0 ? (
                      <p className="text-xs text-gray-400">No downloadable attachments found.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {(attachmentsCache[doc.id] || []).map(att => (
                          <button
                            key={att.id}
                            onClick={(e) => { e.stopPropagation(); handleDownloadAttachment(doc, att); }}
                            disabled={downloadingAttachment === att.id}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-amber-300 rounded-lg text-xs text-gray-700 hover:bg-amber-50 transition-colors disabled:opacity-50"
                          >
                            {downloadingAttachment === att.id
                              ? <FiLoader className="w-3.5 h-3.5 animate-spin" />
                              : <FiDownload className="w-3.5 h-3.5 text-amber-600" />}
                            <span className="truncate max-w-[180px]">{att.name}</span>
                            {att.size && <span className="text-gray-400">({att.size < 1048576 ? `${(att.size/1024).toFixed(0)} KB` : `${(att.size/1048576).toFixed(1)} MB`})</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <p className="text-xs text-gray-400">Source: {doc.file_name}</p>
              </div>
            )}
          </div>
        );
      })}

      {rendered.length < displayed.length && (
        <div className="flex justify-center pt-2">
          <button
            onClick={() => setRenderLimit(prev => prev + RENDER_PAGE)}
            className="px-5 py-2 text-sm font-medium text-green-700 border border-green-300 rounded-lg hover:bg-green-50 transition-colors"
          >
            Load more ({displayed.length - rendered.length} remaining)
          </button>
        </div>
      )}

      {emailDetailDoc && (
        <EmailDetailModal
          mailboxId={emailDetailDoc.ai_raw?.mailbox_id}
          messageId={emailDetailDoc.external_id}
          subject={emailDetailDoc.file_name}
          onClose={() => setEmailDetailDoc(null)}
        />
      )}
    </div>
  );
}

// ─── AI Report tab ────────────────────────────────────────────────────────────

function DocRefChip({ num, doc, onViewEmail }) {
  const [downloading, setDownloading] = useState(false);

  const handleClick = async () => {
    if (!doc) return;
    if (doc.source === 'exchange') {
      if (doc.ai_raw?.mailbox_id) {
        onViewEmail(doc);
      } else {
        window.open(doc.file_url, '_blank', 'noopener,noreferrer');
      }
      return;
    }
    // SharePoint / Doxolve — view via backend proxy, falling back to the source link directly
    // since window.open can't carry the Authorization header our backend endpoint requires.
    setDownloading(true);
    try {
      const token = localStorage.getItem('access_token');
      const url = businessIntelligenceAPI.getDocumentDownloadUrl(doc.id, true);
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`Server returned ${res.status}`);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (err) {
      console.error('View document error:', err);
      if (doc.file_url) window.open(doc.file_url, '_blank', 'noopener,noreferrer');
    }
    finally { setDownloading(false); }
  };

  if (!doc) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-500 font-mono">[{num}]</span>
    );
  }

  const isExchange = doc.source === 'exchange';
  return (
    <button
      onClick={handleClick}
      disabled={downloading}
      title={doc.file_name}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors font-mono disabled:opacity-50 cursor-pointer"
    >
      {isExchange ? <FiMail className="w-2.5 h-2.5" /> : <FiFileText className="w-2.5 h-2.5" />}
      [{num}]
    </button>
  );
}

function ReportText({ text, documents, onViewEmail }) {
  // Split text on [N] or [N, M, ...] patterns and render doc ref chips inline
  const parts = [];
  const refPattern = /\[(\d+(?:,\s*\d+)*)\]/g;
  let last = 0;
  let match;

  while ((match = refPattern.exec(text)) !== null) {
    if (match.index > last) {
      parts.push({ type: 'text', value: text.slice(last, match.index) });
    }
    const nums = match[1].split(',').map(n => parseInt(n.trim(), 10));
    parts.push({ type: 'refs', nums });
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    parts.push({ type: 'text', value: text.slice(last) });
  }

  return (
    <>
      {parts.map((part, i) => {
        if (part.type === 'text') return <span key={i}>{part.value}</span>;
        return (
          <span key={i} className="inline-flex items-center gap-0.5 mx-0.5 flex-wrap">
            {part.nums.map(num => (
              <DocRefChip
                key={num}
                num={num}
                doc={documents[num - 1] || null}
                onViewEmail={onViewEmail}
              />
            ))}
          </span>
        );
      })}
    </>
  );
}

function AiReportTab({ session, documents, onGenerate, generating, reportProgress, onViewEmail }) {
  const report = session.ai_report;

  // Custom markdown components to intercept text nodes and render doc ref chips
  const makeComponents = (docs, viewEmail) => ({
    p: ({ children }) => (
      <p className="text-gray-700 leading-relaxed my-2">
        {renderWithRefs(children, docs, viewEmail)}
      </p>
    ),
    li: ({ children }) => (
      <li className="text-gray-700 my-0.5">
        {renderWithRefs(children, docs, viewEmail)}
      </li>
    ),
  });

  function renderWithRefs(children, docs, viewEmail) {
    return Array.isArray(children)
      ? children.map((child, i) =>
          typeof child === 'string'
            ? <ReportText key={i} text={child} documents={docs} onViewEmail={viewEmail} />
            : child
        )
      : typeof children === 'string'
        ? <ReportText text={children} documents={docs} onViewEmail={viewEmail} />
        : children;
  }

  if (!report && !generating) {
    return (
      <div className="text-center py-16">
        <FiBarChart2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600 mb-1 font-medium">No report generated yet</p>
        <p className="text-sm text-gray-500 mb-6">
          {session.status === 'complete'
            ? 'The report is auto-generated after AI processing. Click below to generate it now.'
            : 'Complete AI processing first, then generate the report.'}
        </p>
        <button
          onClick={onGenerate}
          disabled={session.status !== 'complete'}
          className="inline-flex items-center gap-2 px-6 py-3 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
        >
          <BsRobot className="w-4 h-4" />
          Generate AI Report
        </button>
      </div>
    );
  }

  if (generating) {
    const chunksTotal = reportProgress?.chunksTotal || Math.ceil(documents.length / 50);
    const chunksComplete = reportProgress?.chunksComplete || 0;
    const isCombining = reportProgress && chunksComplete >= chunksTotal;
    const pct = chunksTotal > 0 ? Math.round((chunksComplete / chunksTotal) * 100) : 0;
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <FiLoader className="w-8 h-8 animate-spin text-purple-600" />
        {reportProgress ? (
          <>
            <p className="text-sm text-gray-600 font-medium">
              {isCombining ? 'Combining batch reports…' : `Analysing batch ${chunksComplete + 1} of ${chunksTotal}…`}
            </p>
            <div className="w-64">
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-purple-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
              </div>
              <p className="text-xs text-gray-400 mt-1 text-center">{chunksComplete} of {chunksTotal} batches complete</p>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-600">Starting report generation for {documents.length} documents…</p>
            <p className="text-xs text-gray-400">Large datasets are split into batches automatically</p>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">Generated by Claude Haiku — based on {documents.length} documents · click <span className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded bg-green-50 text-green-700 border border-green-200 font-mono text-xs">[N]</span> to open document</p>
        <button
          onClick={onGenerate}
          disabled={generating}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <FiRefreshCw className="w-3.5 h-3.5" />
          Regenerate
        </button>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl p-6 prose prose-sm max-w-none
        prose-headings:text-gray-800 prose-headings:font-semibold
        prose-h2:text-base prose-h2:border-b prose-h2:border-gray-200 prose-h2:pb-2 prose-h2:mt-6
        prose-h3:text-sm prose-h3:text-gray-700
        prose-strong:text-gray-900
        prose-ul:my-2">
        <ReactMarkdown components={makeComponents(documents, onViewEmail)}>{report}</ReactMarkdown>
      </div>
    </div>
  );
}

// ─── Missing Docs tab ─────────────────────────────────────────────────────────

function MissingDocsTab({ session, onGenerate, generating }) {
  const report = session.missing_docs_report;
  const hasAiReport = !!session.ai_report;

  if (!report && !generating) {
    return (
      <div className="text-center py-16">
        <FiAlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600 mb-1 font-medium">No missing documents report yet</p>
        <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
          {hasAiReport
            ? 'Analyses which required document types are missing or outstanding for this run, using the AI Report and the group\'s required Document Types.'
            : 'Generate the AI Report first — this analysis builds on it.'}
        </p>
        <button
          onClick={onGenerate}
          disabled={!hasAiReport}
          className="inline-flex items-center gap-2 px-6 py-3 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
        >
          <FiAlertTriangle className="w-4 h-4" />
          Generate Missing Docs Report
        </button>
      </div>
    );
  }

  if (generating) {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <FiLoader className="w-8 h-8 animate-spin text-amber-600" />
        <p className="text-sm text-gray-600">Analysing required document coverage…</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">Generated by Claude Haiku — based on the AI Report and this run's required Document Types</p>
        <button
          onClick={onGenerate}
          disabled={generating}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          <FiRefreshCw className="w-3.5 h-3.5" />
          Regenerate
        </button>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl p-6 prose prose-sm max-w-none
        prose-headings:text-gray-800 prose-headings:font-semibold
        prose-h2:text-base prose-h2:border-b prose-h2:border-gray-200 prose-h2:pb-2 prose-h2:mt-6
        prose-h3:text-sm prose-h3:text-gray-700
        prose-strong:text-gray-900
        prose-ul:my-2">
        <ReactMarkdown>{report}</ReactMarkdown>
      </div>
    </div>
  );
}

// ─── Overview tab ─────────────────────────────────────────────────────────────

function OverviewTab({ session, documents, onSync, onResync, onAccept, onPromote, onReprocess, syncing, resyncing, accepting, promoting, reprocessing, customDocTypes = [] }) {
  const mappings = session.group_folder_mappings || [];
  const syncSummary = session.sync_summary || {};

  const aiCounts = {
    pending:    documents.filter(d => d.ai_status === 'pending').length,
    processing: documents.filter(d => d.ai_status === 'processing').length,
    complete:   documents.filter(d => d.ai_status === 'complete').length,
    error:      documents.filter(d => d.ai_status === 'error').length,
  };
  const aiTotal = documents.length;
  const aiDone = aiCounts.complete + aiCounts.error;
  const aiPercent = aiTotal > 0 ? Math.round((aiDone / aiTotal) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex items-center gap-3 flex-wrap">
        {session.stage === 'rfq' && (
          <button
            onClick={onPromote}
            disabled={promoting}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-600 text-white text-sm font-medium rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50"
          >
            {promoting ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiArrowRight className="w-4 h-4" />}
            {promoting ? 'Promoting…' : 'Promote to WIP'}
          </button>
        )}
        {(session.status === 'pending' || session.status === 'error') && (
          <button
            onClick={onSync}
            disabled={syncing}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {syncing ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiPlay className="w-4 h-4" />}
            {syncing ? 'Syncing…' : 'Start Sync'}
          </button>
        )}
        {session.status === 'complete' && (
          <button
            onClick={onAccept}
            disabled={accepting}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            {accepting ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiCheckCircle className="w-4 h-4" />}
            {accepting ? 'Accepting…' : 'Accept All'}
          </button>
        )}
        {['complete', 'error', 'processing'].includes(session.status) && (
          <button
            onClick={onResync}
            disabled={resyncing || reprocessing || syncing || accepting}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {resyncing ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiRefreshCw className="w-4 h-4" />}
            {resyncing ? 'Resyncing…' : 'Re Sync'}
          </button>
        )}
        {['complete', 'error', 'processing'].includes(session.status) && (
          <button
            onClick={onReprocess}
            disabled={reprocessing || resyncing || syncing || accepting}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {reprocessing ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiRefreshCw className="w-4 h-4" />}
            {reprocessing ? 'Reprocessing…' : 'Reprocess AI'}
          </button>
        )}
      </div>

      {/* Pipeline steps */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Pipeline Status</h3>
        <div className="flex items-center gap-4 flex-wrap">
          {[
            { label: 'Sync',       done: ['processing','complete','archived'].includes(session.status), active: session.status === 'syncing' },
            { label: 'AI Process', done: ['complete','archived'].includes(session.status),               active: session.status === 'processing' },
            { label: 'Report',     done: !!session.ai_report,                                             active: false },
            { label: 'Complete',   done: ['complete','archived'].includes(session.status),                active: false },
          ].map(step => (
            <div key={step.label} className={`flex items-center gap-1.5 text-sm ${step.done ? 'text-green-600' : step.active ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>
              {step.done ? <FiCheckCircle className="w-4 h-4" /> : step.active ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiClock className="w-4 h-4" />}
              {step.label}
            </div>
          ))}
        </div>
      </div>

      {/* AI processing progress */}
      {aiTotal > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">AI Processing</h3>
          <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
            <div className="h-full bg-green-500 rounded-full transition-all" style={{ width: `${aiPercent}%` }} />
          </div>
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span className="text-green-600">{aiCounts.complete} complete</span>
            {aiCounts.processing > 0 && <span className="text-purple-600">{aiCounts.processing} processing</span>}
            {aiCounts.pending > 0 && <span>{aiCounts.pending} pending</span>}
            {aiCounts.error > 0 && <span className="text-red-500">{aiCounts.error} error</span>}
            <span className="ml-auto">{aiPercent}%</span>
          </div>
        </div>
      )}

      {/* Folder mappings */}
      {mappings.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Folder Mappings</h3>
          <div className="space-y-2">
            {mappings.map((mapping, idx) => {
              const SourceIcon = SOURCE_ICONS[mapping.source] || FiFileText;
              const folderSummary = syncSummary[`mapping_${idx}`] || {};
              return (
                <div key={idx} className="flex items-center gap-3 py-2 border-b border-gray-100 last:border-0">
                  <SourceIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-gray-700 truncate">{mapping.folder_name || mapping.folder_id || 'Folder'}</span>
                      {(mapping.types?.length ? mapping.types : (mapping.type ? [mapping.type] : [])).map(tv => (
                        <DocTypeBadge key={tv} type={tv} customDocTypes={customDocTypes} />
                      ))}
                    </div>
                    {folderSummary.synced != null && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {folderSummary.synced} synced
                        {folderSummary.failed > 0 && <span className="text-red-500 ml-2">{folderSummary.failed} failed</span>}
                        {folderSummary.skipped > 0 && <span className="text-gray-400 ml-2">{folderSummary.skipped} skipped</span>}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function BusinessIntelligenceSession() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [syncing, setSyncing] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportProgress, setReportProgress] = useState(null); // { chunksComplete, chunksTotal }
  const [generatingMissingDocs, setGeneratingMissingDocs] = useState(false);
  const [emailDetailDoc, setEmailDetailDoc] = useState(null);
  const loopRef = useRef(false);

  const loadSession = useCallback(async () => {
    try {
      const res = await businessIntelligenceAPI.getSession(id);
      setSession(res.data.session);
      setDocuments(res.data.documents || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadSession(); }, [loadSession]);

  useEffect(() => {
    if (!session) return;
    if (!['syncing', 'processing'].includes(session.status)) return;
    const timer = setInterval(loadSession, 4000);
    return () => clearInterval(timer);
  }, [session, loadSession]);

  // Auto-resume process loop if page loads/reloads with unfinished AI processing
  const autoResumedRef = useRef(false);
  useEffect(() => {
    if (!session || !documents) return;
    if (autoResumedRef.current) return;
    if (!['syncing', 'processing'].includes(session.status)) return;
    const hasPending = documents.some(d => d.ai_status === 'pending');
    if (!hasPending) return;
    autoResumedRef.current = true;
    startProcessLoop().catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.id, session?.status, documents?.length]);

  const handleResync = async () => {
    if (loopRef.current) return;
    loopRef.current = true;
    setResyncing(true);
    try {
      await businessIntelligenceAPI.resyncSession(id);
      await loadSession();
      const res = await businessIntelligenceAPI.getSession(id);
      const mappings = res.data.session?.group_folder_mappings || [];
      for (let i = 0; i < mappings.length; i++) {
        await businessIntelligenceAPI.syncSession(id, i);
        await loadSession();
      }
      await startProcessLoop();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      loopRef.current = false;
      setResyncing(false);
    }
  };

  const startProcessLoop = async () => {
    let result;
    let consecutiveErrors = 0;
    do {
      try {
        result = await businessIntelligenceAPI.processSession(id);
        consecutiveErrors = 0;
        await loadSession();
      } catch (callErr) {
        consecutiveErrors++;
        if (consecutiveErrors >= 5) throw callErr; // give up after 5 consecutive failures
        await new Promise(r => setTimeout(r, 2000)); // brief pause before retry
        await loadSession();
        continue;
      }
    } while ((result?.data?.remaining || 0) > 0);
    await loadSession();
    // Auto-generate report after processing completes (chunked loop)
    if ((result?.data?.totalCount || 0) > 0) {
      try {
        setGeneratingReport(true);
        setReportProgress(null);
        let reportResult;
        do {
          reportResult = await businessIntelligenceAPI.generateReport(id);
          if (!reportResult.data.done) {
            setReportProgress({ chunksComplete: reportResult.data.chunksComplete, chunksTotal: reportResult.data.chunksTotal });
          }
        } while (!reportResult.data.done);
        await loadSession();
      } catch (err) {
        // Non-fatal — user can generate manually from the Report tab
      } finally {
        setGeneratingReport(false);
        setReportProgress(null);
      }
    }
  };

  const handleSync = async () => {
    if (loopRef.current) return;
    loopRef.current = true;
    setSyncing(true);
    try {
      const res = await businessIntelligenceAPI.getSession(id);
      const mappings = res.data.session?.group_folder_mappings || [];
      const total = mappings.length;
      if (total === 0) {
        setError('No folder mappings configured for this group.');
        return;
      }
      for (let i = 0; i < total; i++) {
        await businessIntelligenceAPI.syncSession(id, i);
        await loadSession();
      }
      await startProcessLoop();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      loopRef.current = false;
      setSyncing(false);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await businessIntelligenceAPI.acceptSession(id);
      await loadSession();
      // Export loop: copy each accepted doc to the archive destination
      try {
        let exportResult;
        let first = true;
        do {
          exportResult = await businessIntelligenceAPI.exportSession(id, first);
          first = false;
        } while (!exportResult.data.done);
      } catch (exportErr) {
        // Non-fatal — export errors show on individual docs
        console.warn('[Accept] Export loop error:', exportErr.message);
      }
      await loadSession();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setAccepting(false);
    }
  };

  const handlePromoteToWip = async () => {
    setPromoting(true);
    try {
      await businessIntelligenceAPI.promoteToWip(id);
      await loadSession();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setPromoting(false);
    }
  };

  const handleReprocess = async () => {
    if (loopRef.current) return;
    loopRef.current = true;
    setReprocessing(true);
    try {
      await businessIntelligenceAPI.reprocessSession(id);
      await loadSession();
      await startProcessLoop();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      loopRef.current = false;
      setReprocessing(false);
    }
  };

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    setReportProgress(null);
    try {
      let result;
      do {
        result = await businessIntelligenceAPI.generateReport(id);
        if (!result.data.done) {
          setReportProgress({ chunksComplete: result.data.chunksComplete, chunksTotal: result.data.chunksTotal });
        }
      } while (!result.data.done);
      await loadSession();
      setActiveTab('report');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setGeneratingReport(false);
      setReportProgress(null);
    }
  };

  const handleGenerateMissingDocsReport = async () => {
    setGeneratingMissingDocs(true);
    try {
      await businessIntelligenceAPI.generateMissingDocsReport(id);
      await loadSession();
      setActiveTab('missing-docs');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setGeneratingMissingDocs(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center py-16">
      <FiLoader className="w-8 h-8 animate-spin text-green-600" />
    </div>
  );

  if (!session) return (
    <div className="max-w-4xl mx-auto px-4 py-12 text-center">
      <FiAlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
      <p className="text-gray-600">{error || 'Session not found.'}</p>
      <button onClick={() => navigate('/business-intelligence')} className="mt-4 text-sm text-green-600 hover:underline">
        Back to Business Intelligence
      </button>
    </div>
  );

  const aiCompleteCount = documents.filter(d => d.ai_status === 'complete').length;

  const tabs = [
    { id: 'overview',   label: 'Overview',                        icon: FiArchive },
    { id: 'documents',  label: `Documents (${documents.length})`, icon: FiFileText },
    { id: 'ai',         label: `AI Processed (${aiCompleteCount})`, icon: BsRobot },
    { id: 'report',     label: 'AI Report',                       icon: FiBarChart2 },
    { id: 'missing-docs', label: 'Missing Docs',                  icon: FiAlertTriangle },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <button
          onClick={() => navigate('/business-intelligence')}
          className="mt-1 p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
        >
          <FiArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          {session.group_name && (
            <p className="text-xs font-medium text-green-700 uppercase tracking-wide mb-0.5">{session.group_name}</p>
          )}
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <FiArchive className="w-6 h-6 text-green-600" />
              {session.name}
            </h1>
            <StatusBadge status={session.status} />
            {session.stage === 'rfq' && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700">
                <FiFileText className="w-4 h-4" />
                RFQ
              </span>
            )}
          </div>
          <div className="flex items-center gap-4 mt-1 text-xs text-gray-500 flex-wrap">
            {formatPeriod(session.period_start, session.period_end) && (
              <span className="flex items-center gap-1">
                <FiCalendar className="w-3 h-3" />
                {formatPeriod(session.period_start, session.period_end)}
              </span>
            )}
            {session.references?.length > 0 && (
              <span className="flex items-center gap-1">
                <FiTag className="w-3 h-3" />
                {session.references.join(', ')}
              </span>
            )}
            <span>Created {formatDateTime(session.created_at)}</span>
          </div>
        </div>
        <button
          onClick={loadSession}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
          title="Refresh"
        >
          <FiRefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Error banner */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <FiAlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">Error</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-600">
            <FiX className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Session error message */}
      {session.error_message && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2 text-sm text-amber-800">
          <FiAlertTriangle className="w-4 h-4 flex-shrink-0" />
          {session.error_message}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Documents',  value: documents.length || 0 },
          { label: 'AI Complete', value: aiCompleteCount },
          { label: 'AI Errors',  value: documents.filter(d => d.ai_status === 'error').length },
          { label: 'Accepted',   value: documents.filter(d => d.review_status === 'accepted').length },
        ].map(stat => (
          <div key={stat.label} className="bg-white border border-gray-200 rounded-xl p-4 text-center">
            <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
            <p className="text-xs text-gray-500 mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-green-600 text-green-700'
                    : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
                {tab.id === 'report' && session.ai_report && (
                  <span className="w-2 h-2 rounded-full bg-green-500 ml-1" title="Report available" />
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'overview' && (
        <OverviewTab
          session={session}
          documents={documents}
          onSync={handleSync}
          onResync={handleResync}
          onAccept={handleAccept}
          onPromote={handlePromoteToWip}
          onReprocess={handleReprocess}
          syncing={syncing}
          resyncing={resyncing}
          accepting={accepting}
          promoting={promoting}
          reprocessing={reprocessing}
          customDocTypes={session.group_custom_doc_types || []}
        />
      )}

      {activeTab === 'documents' && (
        <DocumentsTab
          sessionId={id}
          sessionStatus={session.status}
          customDocTypes={session.group_custom_doc_types || []}
          excludedDocTypes={session.group_excluded_doc_types || []}
        />
      )}

      {activeTab === 'ai' && (
        <AiProcessedTab
          sessionId={id}
          sessionStatus={session.status}
          customDocTypes={session.group_custom_doc_types || []}
          excludedDocTypes={session.group_excluded_doc_types || []}
        />
      )}

      {activeTab === 'report' && (
        <AiReportTab
          session={session}
          documents={documents}
          onGenerate={handleGenerateReport}
          generating={generatingReport}
          reportProgress={reportProgress}
          onViewEmail={setEmailDetailDoc}
        />
      )}

      {activeTab === 'missing-docs' && (
        <MissingDocsTab
          session={session}
          onGenerate={handleGenerateMissingDocsReport}
          generating={generatingMissingDocs}
        />
      )}

      {emailDetailDoc && (
        <EmailDetailModal
          mailboxId={emailDetailDoc.ai_raw?.mailbox_id}
          messageId={emailDetailDoc.external_id}
          subject={emailDetailDoc.file_name}
          onClose={() => setEmailDetailDoc(null)}
        />
      )}
    </div>
  );
}
